import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Button, BUTTONTYPE } from "src/components/atoms/Button";
import { Input } from "src/components/atoms/Input";
import { Msg, msg } from "src/i18n/Msg";
import { validators } from "src/utils/validators";
import styles from "src/components/forms/AddEventForm/styles.module.css";
import { Check } from "src/components/atoms/Check";
import Plus from "src/assets/icons/plusPurple.svg";
import Download from "src/assets/images/userDownload.svg";
import { useFormik } from "src/hooks/useFormik";
import { useCreateEvent } from "src/queries/useCreateEvent";
import { Loader } from "src/components/atoms/Loader";
import { CalendarInput } from "src/components/molecules/CalendarInput";
import { format } from "date-fns";
import { validateDate } from "src/utils/helpers";
import { DictionaryKey } from "src/types/language";
import { PAGES } from "src/types/navigation";
import { RESPONSSTAUTS } from "src/types/api";
import EditIcon from "src/assets/icons/edit.svg";
import TrashIcon from "src/assets/icons/trash.svg";
import { v4 as uuidv4 } from "uuid";

type ResultItem = {
  id: string;
  label: string;
};

type Props = {
  handleAddDeatil: () => void;
  isShowDetailModal: boolean;
  close: () => void;
  handleClickResult: (name: string) => void;
  setShowSuccessResult: React.Dispatch<React.SetStateAction<boolean>>;
};

const MAX_RESULT_COUNT = 2;

export const AddEventForm: React.FC<Props> = ({
  isShowDetailModal,
  handleAddDeatil,
  handleClickResult,
  setShowSuccessResult,
}) => {
  const intl = useIntl();
  const {
    createEventMutate,
    errorMessage,
    setErrorMessage,
    isPending,
    formError,
    setFormError,
  } = useCreateEvent();

  const [editingResultId, setEditingResultId] = useState<string | null>(null);
  const [dateError, setDateError] = useState(false);

  const { handleSubmit, setFieldValue, values, errors } = useFormik({
    initialValues: {
      name: "",
      date: "",
      results: [] as ResultItem[],
      terms: false,
      resultName: "",
      description: "",
      file: null,
      previewImg: "",
    },
    validationSchema: validators.addEvent,
    onSubmit: (body) => {
      const [day, month, year] = body.date.split(".");
      const formattedDate = new Date(`${year}-${month}-${day}`).toISOString();
      const formData = new FormData();
      const outcomes = body.results.map((item) => ({
        label: item.label,
        is_win: false,
      }));
      formData.append("title", body.name);
      formData.append("description", body.description);
      formData.append("deadline", formattedDate);
      formData.append("outcomes", JSON.stringify(outcomes));
      formData.append("type", "new");
      if (body.file) {
        formData.append("event_image", body.file);
      }
      createEventMutate(formData, {
        onSuccess: (data) => {
          if (data.status === RESPONSSTAUTS.SUCCESS_CREATE) {
            setShowSuccessResult(true);
          }
        },
      });
    },
  });

  const handleChange = (
    key: string,
    value: string | boolean | ResultItem[] | Blob
  ) => {
    if (
      formError.title ||
      formError.description ||
      formError?.outcomes ||
      formError.deadline ||
      formError.globalError
    ) {
      setFormError((prev) => ({
        ...prev,
        description: "",
        title: "",
        deadline: "",
        globalError: [],
      }));
      setErrorMessage("");
    }
    setFieldValue(key, value);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const file = e.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      handleChange("previewImg", imageUrl);
      handleChange("file", file);
    }
  };

  if (isPending) {
    return (
      <div className={styles.loader}>
        <Loader />
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {isShowDetailModal ? (
        <div className={styles.resultData}>
          <Input
            value={values.resultName}
            onChange={(e) => {
              if (formError.outcomes) {
                setFormError((prev) => ({
                  ...prev,
                  outcomes: "",
                }));
              }
              handleChange("resultName", e.target.value);
            }}
            error={formError.outcomes}
            placeholder={msg(intl, {
              id: "components.forms.AddEventForm.resultName.placeholder",
            })}
            label={{ id: "components.forms.AddEventForm.resultName.label" }}
          />
          <Button
            onClick={() => {
              const resultsArray = values.results as ResultItem[];
              const trimmed = values.resultName.trim();

              if (!trimmed) return;

              if (trimmed.length > 10) {
                setFormError((prev) => ({
                  ...prev,
                  outcomes: msg(intl, {
                    id: "components.forms.AddEventForm.outcome.maxLength",
                  }),
                }));
                return;
              }

              if (editingResultId) {
                const updatedResults = resultsArray.map((item) =>
                  item.id === editingResultId
                    ? { ...item, label: trimmed }
                    : item
                );
                handleChange("results", updatedResults);
                setEditingResultId(null);
              } else {
                if (resultsArray.length < MAX_RESULT_COUNT) {
                  const newResult = {
                    id: uuidv4(),
                    label: trimmed,
                  };
                  handleChange("results", [...resultsArray, newResult]);
                  handleAddDeatil();
                }
              }

              handleChange("resultName", "");
              handleClickResult("");
            }}
            type="button"
            disabled={!values.resultName}
            text={{
              id: editingResultId
                ? "components.organisms.AddEventForm.save"
                : "components.organisms.AddEventForm.post",
            }}
          />
        </div>
      ) : (
        <div className={styles.formContent}>
          <div className={styles.topRow}>
            <div className={styles.photo}>
              <label htmlFor="file-upload">
                <img
                  src={values.previewImg ? values.previewImg : Download}
                  className={styles.uploadedImage}
                  alt="download"
                />
              </label>
              <input
                id="file-upload"
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleFileUpload}
              />
            </div>
            <Input
              value={values.name}
              onChange={(e) => handleChange("name", e.target.value)}
              error={errors.name || formError.title}
              placeholder={msg(intl, {
                id: "components.forms.AddEventForm.name.placeholder",
              })}
              errorWithoutTranslation={!!formError.title}
              label={{ id: "components.forms.AddEventForm.name.label" }}
            />
          </div>
          <div className={styles.dateWrapper}>
            <CalendarInput
              label={{ id: "components.forms.AddEventForm.date.label" }}
              value={values.date ?? ""}
              onchangeInput={(value) => {
                handleChange("date", value);
                setDateError(!validateDate(value));
              }}
              error={errors.date || formError.deadline}
              calendarChange={(value: Date | null) => {
                const formatDate = value && format(value, "dd.MM.yyyy");
                handleChange("date", formatDate as string);
              }}
              errorWithoutTranslation={!!formError.deadline}
              className={styles.input}
            />
          </div>
          {dateError && (
            <p className="errorText">
              <Msg id="components.forms.AddEventForm.date.format" />
            </p>
          )}
          <div className={styles.result}>
            <h5>
              <Msg id="components.organisms.AddEventForm.result" />
            </h5>
            {values.results?.length > 0 ? (
              <div className={styles.list}>
                {values.results.map((result, index) => (
                  <div
                    className={`${styles.resultItem} ${
                      index === 1 ? styles.resultItemNo : ""
                    }`}
                    key={result.id}
                  >
                    <div className={styles.iconsWrapper}>
                      <img
                        src={TrashIcon}
                        onClick={() => {
                          handleChange(
                            "results",
                            values.results.filter(
                              (item) => item.id !== result.id
                            )
                          );
                        }}
                      />
                      <img
                        src={EditIcon}
                        onClick={() => {
                          handleChange("resultName", result.label);
                          setEditingResultId(result.id);
                          handleClickResult(result.label);
                        }}
                      />
                    </div>
                    {result.label}
                  </div>
                ))}
                {values.results?.length !== MAX_RESULT_COUNT && (
                  <div
                    className={styles.resultItemBtn}
                    role="button"
                    onClick={handleAddDeatil}
                  >
                    +
                  </div>
                )}
              </div>
            ) : (
              <Button
                onClick={handleAddDeatil}
                type="button"
                btnType={BUTTONTYPE.LIGHT}
                className={styles.addOutcomeBtn}
                rightIcon={Plus}
                text={{ id: "components.organisms.AddEventForm.resultBtn" }}
              />
            )}
            {errors.results && values.results?.length < MAX_RESULT_COUNT && (
              <p className="errorText">
                <Msg id={errors.results as DictionaryKey} />
              </p>
            )}
            {formError.outcomes && (
              <p className="errorText">{formError.outcomes}</p>
            )}
          </div>

          <div className={styles.terms}>
            <Check
              isCheck={values.terms}
              onChange={() => handleChange("terms", !values.terms)}
            />
            <p>
              <Msg
                id="components.organisms.RegistrationForm.terms"
                values={{
                  link: (
                    <a
                      href={PAGES.TERMS}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Msg id="components.organisms.RegistrationForm.link" />
                    </a>
                  ),
                }}
              />
            </p>
          </div>
          <Button
            disabled={!values.terms}
            type="submit"
            text={{ id: "components.organisms.AddEventForm.submit" }}
          />
          {formError?.globalError?.length > 0 && (
            <>
              {formError?.globalError?.map((error) => (
                <p className="errorText">{error}</p>
              ))}
              {errorMessage && <p className="errorText">{errorMessage}</p>}
            </>
          )}
        </div>
      )}
    </form>
  );
};
